import { PlanogramBay, PlanogramBayNonCircular } from "./bay";
import { Pog } from "./pog";
import { PlanogramItem } from "./item";
import { PlanogramPegboardPlacement } from "./pegboard";
import { BaseProduct } from "./product";

export interface PlanogramNonCircular {
  bays: PlanogramBayNonCircular[];
}

export class Planogram {
  bays: PlanogramBay[];
  pog: Pog;

  constructor(planogramNonCircular: PlanogramNonCircular, pog: Pog) {
    this.bays = planogramNonCircular.bays.map(
      (bayNonCircular) => new PlanogramBay(bayNonCircular, this),
    );
    this.pog = pog;

    // Merge shelves after all bays and shelves are created.
    for (const bayNonCircular of planogramNonCircular.bays) {
      for (const shelfNonCircular of bayNonCircular.shelves) {
        const shelf = this.getShelf(shelfNonCircular.uniqueId);
        if (!shelf) continue;

        if (shelfNonCircular.mergedLeft) {
          const shelfLeft = this.getShelf(shelfNonCircular.mergedLeft);
          if (shelfLeft) {
            shelf.mergedLeft = shelfLeft;
            shelfLeft.mergedRight = shelf;
          }
        }

        if (shelfNonCircular.mergedRight) {
          const shelfRight = this.getShelf(shelfNonCircular.mergedRight);
          if (shelfRight) {
            shelf.mergedRight = shelfRight;
            shelfRight.mergedLeft = shelf;
          }
        }
      }
    }

    // Make sure shelves are from top to bottom.
    this.bays.forEach((bay) => bay.fixShelvesOrder());
  }

  getBay = (bayId: string) => {
    for (const bay of this.bays) {
      if (bay.uniqueId === bayId) {
        return bay;
      }
    }

    return null;
  };

  getShelf = (shelfId: string) => {
    for (const bay of this.bays) {
      for (const shelf of bay.shelves) {
        if (shelf.uniqueId === shelfId) {
          return shelf;
        }
      }
    }

    return null;
  };

  getItem = (itemId: string) => {
    for (const bay of this.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (item.uniqueId === itemId) {
            return item;
          }
        }
      }
    }

    return null;
  };

  getPegboardPlacement = (placementId: string): PlanogramPegboardPlacement | null => {
    for (const bay of this.bays) {
      for (const pegboard of bay.pegboards) {
        for (const placement of pegboard.placements) {
          if (placement.uniqueId === placementId) {
            return placement;
          }
        }
      }
    }

    return null;
  };

  getItems = (itemIds: string[]) => {
    const items = [];

    for (const itemId of itemIds) {
      items.push(this.getItem(itemId));
    }

    return items;
  };

  addBay = (bay: PlanogramBay, index?: number) => {
    const hasIndex = index !== undefined && index >= 0 && index <= this.bays.length;

    if (hasIndex) {
      const previousBay = this.bays[index - 1];
      if (previousBay) {
        for (const shelf of previousBay.shelves) {
          shelf.unmergeRight();
        }
      }

      this.bays.splice(index, 0, bay);
    } else {
      this.bays.push(bay);
    }

    return bay;
  };

  getTotalSpace = () => {
    let totalSpace = 0;

    for (const bay of this.bays) {
      for (const shelf of bay.shelves) {
        totalSpace += shelf.width;
      }
    }

    return totalSpace;
  };

  getAllItems = () => {
    const allItems: PlanogramItem[] = [];

    for (const bay of this.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          allItems.push(item);
        }
      }
    }

    return allItems;
  };

  getAllPlanogramProducts = () => {
    const allProducts: BaseProduct[] = [];

    for (const bay of this.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          allProducts.push(item);
        }
      }

      for (const pegboard of bay.pegboards) {
        for (const placement of pegboard.placements) {
          allProducts.push(placement.pegProduct);
        }
      }
    }

    return allProducts;
  };

  getAllPegboardPlacements = () => {
    const allPlacements: PlanogramPegboardPlacement[] = [];

    for (const bay of this.bays) {
      for (const pegboard of bay.pegboards) {
        for (const placement of pegboard.placements) {
          allPlacements.push(placement);
        }
      }
    }

    return allPlacements;
  };

  getHeight = () => {
    let height = 0;

    for (const bay of this.bays) {
      height = Math.max(bay.height, height);
      height = Math.max(height, bay.notchOffset + bay.maxNotch * bay.notchSpacing);
    }

    return height;
  };

  /**
   * Get the maximum height needed to render the planogram without cutoff.
   * Returns the larger of: planogram structural height vs max item yTop.
   */
  getMaxHeight = () => {
    const maxItemYTop = this.getAllItems().reduce((maxYTop, item) => {
      return Math.max(maxYTop, item.getCoordinates().yTop);
    }, 0);

    return Math.max(this.getHeight(), maxItemYTop);
  };

  getWidth = () => {
    let width = 0;

    for (const bay of this.bays) {
      width += bay.width;
    }

    return width;
  };
}
